import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, doc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/components/AuthProvider';
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/use-toast';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { ArrowLeft, Building2, Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';

// Form validation schema
const tenantFormSchema = z.object({
  // Company Information
  companyName: z.string().min(2, { message: "Company name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().min(10, { message: "Please enter a valid phone number" }),
  street: z.string().min(1, { message: "Street address is required" }),
  city: z.string().min(1, { message: "City is required" }),
  state: z.string().min(1, { message: "State is required" }),
  postalCode: z.string().min(1, { message: "Postal code is required" }),
  country: z.string().min(1, { message: "Country is required" }),
  
  // Primary Admin
  adminFirstName: z.string().min(1, { message: "First name is required" }),
  adminLastName: z.string().min(1, { message: "Last name is required" }),
  adminEmail: z.string().email({ message: "Please enter a valid email address" }),
  adminPassword: z.string().min(8, { message: "Password must be at least 8 characters" }),
  
  // Plan & Features
  subscriptionPlan: z.enum(["basic", "standard", "premium"]),
  userLimit: z.coerce.number().min(1, { message: "User limit must be at least 1" }),
  enableRecurringInvoices: z.boolean().default(true),
  enableVirtualTerminal: z.boolean().default(true),
  enableUserManagement: z.boolean().default(true),
  
  // Additional Settings
  notes: z.string().optional(),
  isActive: z.boolean().default(true),
});

type TenantFormValues = z.infer<typeof tenantFormSchema>;

// Fix form field typing with explicit type
type FieldProps = {
  field: {
    value: any;
    onChange: (value: any) => void;
    onBlur: () => void;
    name: string;
    ref: React.Ref<any>;
  }
}

const CreateTenant: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("company");
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Always initialize form with default values
  const form = useForm<TenantFormValues>({
    resolver: zodResolver(tenantFormSchema),
    defaultValues: {
      companyName: "",
      email: "",
      phone: "",
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      adminFirstName: "",
      adminLastName: "",
      adminEmail: "",
      adminPassword: "",
      subscriptionPlan: "standard",
      userLimit: 5,
      enableRecurringInvoices: true,
      enableVirtualTerminal: true,
      enableUserManagement: true,
      notes: "",
      isActive: true
    },
  });

  // Check for super_admin role using useEffect instead of conditional rendering
  useEffect(() => {
    // Redirect if not super_admin
    if (user) {
      if (user.role === 'super_admin') {
        setIsAuthorized(true);
      } else {
        navigate('/app');
      }
    }
  }, [user, navigate]);

  // Navigate to next tab
  const goToNextTab = async () => {
    let shouldContinue = false;
    
    if (activeTab === "company") {
      // Validate company tab fields
      const valid = await form.trigger([
        "companyName",
        "email",
        "phone",
        "street",
        "city",
        "state",
        "postalCode",
        "country"
      ]);
      shouldContinue = valid;
      if (valid) setActiveTab("admin");
    } 
    else if (activeTab === "admin") {
      // Validate admin tab fields
      const valid = await form.trigger([
        "adminFirstName",
        "adminLastName",
        "adminEmail",
        "adminPassword"
      ]);
      shouldContinue = valid;
      if (valid) setActiveTab("plan");
    } 
    else if (activeTab === "plan") {
      // Validate plan tab fields
      const valid = await form.trigger([
        "subscriptionPlan",
        "userLimit"
      ]);
      shouldContinue = valid;
      if (valid) setActiveTab("review");
    }
    
    if (!shouldContinue) {
      console.log("Validation failed for this tab");
    }
  };
  
  // Go to previous tab
  const goToPreviousTab = () => {
    if (activeTab === "admin") {
      setActiveTab("company");
    } else if (activeTab === "plan") {
      setActiveTab("admin");
    } else if (activeTab === "review") {
      setActiveTab("plan");
    }
  };

  // Process form submission
  const onSubmit = async (data: TenantFormValues) => {
    if (!user || user.role !== 'super_admin') {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // 1. Create company record
      const companyDocRef = await addDoc(collection(db, 'companies'), {
        name: data.companyName,
        email: data.email,
        phone: data.phone,
        address: {
          street: data.street,
          city: data.city,
          state: data.state,
          postalCode: data.postalCode,
          country: data.country
        },
        status: data.isActive ? 'active' : 'inactive',
        subscriptionPlan: data.subscriptionPlan,
        features: {
          recurringInvoices: data.enableRecurringInvoices,
          virtualTerminal: data.enableVirtualTerminal,
          userManagement: data.enableUserManagement
        },
        userLimit: data.userLimit,
        notes: data.notes,
        platformFee: 0,
        defaultTaxRate: 0,
        enableRecurringInvoices: false,
        enableInstallmentInvoices: false,
        invoiceNumbering: {
          prefix: '',
          nextNumber: 1,
          suffix: '',
          yearInNumber: false,
        },
        emailSettings: {
          fromEmail: 'noreply@invoicer.gg',
          invoiceTemplate: '',
          paymentRequest: '',
          paymentThankYou: '',
          refundIssued: '',
          paymentFailed: '',
        },
        // Define default invoice settings
        invoiceSettings: {
          showLogo: false,
          enablePlatformFee: false,
          industry: 'None',
          invoiceType: 'standard',
          paymentMethods: {
            credit_card: true,
            paypal: false,
            ach: false,
            check: false
          },
          invoiceStartingNumber: 1000,
          enableDatePrefix: false,
          invoicePrefix: '',
          showPhoneOnInvoice: false,
          showEmailOnInvoice: false,
          showNotesOnInvoice: true,
          showTermsOnInvoice: true,
          invoicePhone: '',
          invoiceEmail: '',
          colors: {
            primary: '#0D0D0D',  // R13 G13 B13 in hex
            secondary: '#6B7280',
            headerText: '#111827',
            tableHeader: '#F3F4F6',
            tableHeaderText: '#111827',
            totalText: '#111827',
            linkText: '#FFFFFF',
            buttonBackground: '#22C55E'
          },
          template: 'professional',
          numberFormat: 'INV-{NUMBER}',
          zeroPadding: 5,
          resetNumberSequence: 'never',
          dateFormat: 'YYYY-MM-DD',
          customFooterText: "Thank you for your business! If you have any questions, please do not hesitate to contact us.",
          defaultDueTerms: 'due_on_receipt',
          defaultNotes: "Terms and Conditions:\n\n1. Payment: Full payment is due according to the terms specified on the invoice.",
          showUnpaidWatermark: false,
          showPaidWatermark: false
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // 2. Create admin user account
      let adminUserId = '';
      try {
        // Create auth user
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          data.adminEmail,
          data.adminPassword
        );
        adminUserId = userCredential.user.uid;
        
        // Create user record in Firestore
        await setDoc(doc(db, 'users', adminUserId), {
          email: data.adminEmail,
          displayName: `${data.adminFirstName} ${data.adminLastName}`,
          role: 'admin',
          companyId: companyDocRef.id,
          status: 'active',
          createdAt: serverTimestamp()
        });
        
        // Update company with owner reference
        await updateDoc(companyDocRef, {
          ownerId: adminUserId
        });
        
        toast({
          title: "Tenant created successfully",
          description: `${data.companyName} has been added to your tenants.`,
        });
        
        // Navigate to tenant dashboard
        navigate('/app/tenants');
      } catch (error) {
        console.error("Error creating admin user:", error);
        toast({
          title: "Error creating admin user",
          description: "There was a problem creating the admin user account.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error creating tenant:", error);
      toast({
        title: "Error creating tenant",
        description: "There was a problem creating the tenant.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // If still loading user, show loading state
  if (!user) {
    return <div className="flex items-center justify-center h-64">
      <Skeleton className="h-8 w-8 rounded-full" />
    </div>;
  }

  // If not super_admin, don't render the component
  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="container max-w-4xl mx-auto py-8 space-y-8">
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/app/tenants')}
          className="h-8 gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Tenants</span>
        </Button>
      </div>
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Create New Tenant</h1>
          <p className="text-muted-foreground">
            Set up a new company with admin user and subscription plan
          </p>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <CardTitle>New Tenant Details</CardTitle>
          </div>
          <CardDescription>
            Fill out the information below to create a new tenant
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="w-full grid grid-cols-4">
              <TabsTrigger value="company">Company</TabsTrigger>
              <TabsTrigger value="admin">Admin User</TabsTrigger>
              <TabsTrigger value="plan">Plan & Features</TabsTrigger>
              <TabsTrigger value="review">Review & Create</TabsTrigger>
            </TabsList>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <TabsContent value="company" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="companyName"
                      render={({ field }: FieldProps) => (
                        <FormItem className="col-span-2">
                          <FormLabel>Company Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter company name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }: FieldProps) => (
                        <FormItem>
                          <FormLabel>Company Email</FormLabel>
                          <FormControl>
                            <Input placeholder="contact@company.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }: FieldProps) => (
                        <FormItem>
                          <FormLabel>Company Phone</FormLabel>
                          <FormControl>
                            <Input placeholder="(555) 123-4567" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="street"
                      render={({ field }: FieldProps) => (
                        <FormItem className="col-span-2">
                          <FormLabel>Street Address</FormLabel>
                          <FormControl>
                            <Input placeholder="123 Main St" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }: FieldProps) => (
                        <FormItem>
                          <FormLabel>City</FormLabel>
                          <FormControl>
                            <Input placeholder="City" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }: FieldProps) => (
                        <FormItem>
                          <FormLabel>State/Province</FormLabel>
                          <FormControl>
                            <Input placeholder="State" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="postalCode"
                      render={({ field }: FieldProps) => (
                        <FormItem>
                          <FormLabel>Postal Code</FormLabel>
                          <FormControl>
                            <Input placeholder="12345" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="country"
                      render={({ field }: FieldProps) => (
                        <FormItem>
                          <FormLabel>Country</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a country" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="USA">United States</SelectItem>
                              <SelectItem value="CAN">Canada</SelectItem>
                              <SelectItem value="GBR">United Kingdom</SelectItem>
                              <SelectItem value="AUS">Australia</SelectItem>
                              <SelectItem value="NZL">New Zealand</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button type="button" onClick={goToNextTab}>
                      Continue to Admin User
                    </Button>
                  </div>
                </TabsContent>
                
                <TabsContent value="admin" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="adminFirstName"
                      render={({ field }: FieldProps) => (
                        <FormItem>
                          <FormLabel>First Name</FormLabel>
                          <FormControl>
                            <Input placeholder="John" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="adminLastName"
                      render={({ field }: FieldProps) => (
                        <FormItem>
                          <FormLabel>Last Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Doe" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="adminEmail"
                      render={({ field }: FieldProps) => (
                        <FormItem className="col-span-2">
                          <FormLabel>Email Address</FormLabel>
                          <FormControl>
                            <Input 
                              type="email" 
                              placeholder="admin@company.com" 
                              {...field} 
                            />
                          </FormControl>
                          <FormDescription>
                            This will be used for login and password recovery.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="adminPassword"
                      render={({ field }: FieldProps) => (
                        <FormItem className="col-span-2">
                          <FormLabel>Initial Password</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              placeholder="Enter a secure password" 
                              {...field} 
                            />
                          </FormControl>
                          <FormDescription>
                            Admin can change the password after first login.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="flex justify-between gap-2">
                    <Button type="button" variant="outline" onClick={goToPreviousTab}>
                      Back to Company
                    </Button>
                    <Button type="button" onClick={goToNextTab}>
                      Continue to Plan & Features
                    </Button>
                  </div>
                </TabsContent>
                
                <TabsContent value="plan" className="space-y-4">
                  <FormField
                    control={form.control}
                    name="subscriptionPlan"
                    render={({ field }: FieldProps) => (
                      <FormItem>
                        <FormLabel>Subscription Plan</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a plan" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="basic">Basic</SelectItem>
                            <SelectItem value="standard">Standard</SelectItem>
                            <SelectItem value="premium">Premium</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          {field.value === 'basic' && "Includes basic invoicing features and 3 users."}
                          {field.value === 'standard' && "Includes advanced features and up to 10 users."}
                          {field.value === 'premium' && "All features, unlimited users, priority support."}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="userLimit"
                    render={({ field }: FieldProps) => (
                      <FormItem>
                        <FormLabel>User Limit</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            min="1" 
                            placeholder="5" 
                            {...field} 
                          />
                        </FormControl>
                        <FormDescription>
                          Maximum number of users this tenant can create
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="space-y-4 border rounded-md p-4">
                    <h3 className="font-medium">Enabled Features</h3>
                    
                    <FormField
                      control={form.control}
                      name="enableRecurringInvoices"
                      render={({ field }: FieldProps) => (
                        <FormItem className="flex items-center justify-between space-y-0 rounded-md border p-3">
                          <div>
                            <FormLabel className="font-normal">Recurring Invoices</FormLabel>
                            <FormDescription>
                              Allow the tenant to create recurring invoices
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="enableVirtualTerminal"
                      render={({ field }: FieldProps) => (
                        <FormItem className="flex items-center justify-between space-y-0 rounded-md border p-3">
                          <div>
                            <FormLabel className="font-normal">Virtual Terminal</FormLabel>
                            <FormDescription>
                              Allow the tenant to take payments via virtual terminal
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="enableUserManagement"
                      render={({ field }: FieldProps) => (
                        <FormItem className="flex items-center justify-between space-y-0 rounded-md border p-3">
                          <div>
                            <FormLabel className="font-normal">User Management</FormLabel>
                            <FormDescription>
                              Allow the tenant admin to create and manage users
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }: FieldProps) => (
                      <FormItem>
                        <FormLabel>Notes (Internal)</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Add any notes about this tenant (only visible to super admins)" 
                            className="h-24"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="isActive"
                    render={({ field }: FieldProps) => (
                      <FormItem className="flex items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Activate tenant immediately
                          </FormLabel>
                          <FormDescription>
                            When enabled, the tenant will be able to access the system immediately.
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                  
                  <div className="flex justify-between gap-2">
                    <Button type="button" variant="outline" onClick={goToPreviousTab}>
                      Back to Admin User
                    </Button>
                    <Button type="button" onClick={goToNextTab}>
                      Review & Create
                    </Button>
                  </div>
                </TabsContent>
                
                <TabsContent value="review" className="space-y-4">
                  <div className="border rounded-md p-4 space-y-4">
                    <h3 className="font-medium">Company Information</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-muted-foreground">Company Name</div>
                      <div>{form.getValues().companyName}</div>
                      
                      <div className="text-muted-foreground">Email</div>
                      <div>{form.getValues().email}</div>
                      
                      <div className="text-muted-foreground">Phone</div>
                      <div>{form.getValues().phone}</div>
                      
                      <div className="text-muted-foreground">Address</div>
                      <div>
                        {form.getValues().street}, {form.getValues().city}, {form.getValues().state} {form.getValues().postalCode}, {form.getValues().country}
                      </div>
                    </div>
                  </div>
                  
                  <div className="border rounded-md p-4 space-y-4">
                    <h3 className="font-medium">Admin User</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-muted-foreground">Name</div>
                      <div>{form.getValues().adminFirstName} {form.getValues().adminLastName}</div>
                      
                      <div className="text-muted-foreground">Email</div>
                      <div>{form.getValues().adminEmail}</div>
                      
                      <div className="text-muted-foreground">Password</div>
                      <div>********</div>
                    </div>
                  </div>
                  
                  <div className="border rounded-md p-4 space-y-4">
                    <h3 className="font-medium">Plan & Features</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-muted-foreground">Subscription Plan</div>
                      <div className="capitalize">{form.getValues().subscriptionPlan}</div>
                      
                      <div className="text-muted-foreground">User Limit</div>
                      <div>{form.getValues().userLimit}</div>
                      
                      <div className="text-muted-foreground">Active Status</div>
                      <div>{form.getValues().isActive ? 'Active' : 'Inactive'}</div>
                      
                      <div className="text-muted-foreground">Features</div>
                      <div>
                        {form.getValues().enableRecurringInvoices && <div>✓ Recurring Invoices</div>}
                        {form.getValues().enableVirtualTerminal && <div>✓ Virtual Terminal</div>}
                        {form.getValues().enableUserManagement && <div>✓ User Management</div>}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between gap-2">
                    <Button type="button" variant="outline" onClick={goToPreviousTab}>
                      Back to Plan & Features
                    </Button>
                    <Button 
                      type="submit" 
                      className="gap-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                      Create Tenant
                    </Button>
                  </div>
                </TabsContent>
              </form>
            </Form>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateTenant; 